import {
  Button,
  CheckboxInput,
  RadioInput,
  Row,
  SelectInput,
  TextInput,
  useButtonConfirmDialog,
  useForm,
} from '@rocketmakers/armstrong';
import * as React from 'react';
import { useParams } from 'react-router-dom';
import {
  DeviceType,
  HeatFlowMeterCircuit,
  RuleActionType,
  RuleCategory,
} from '../../../../../backend/shared/api-clients/_api/config';
import { HeatFlowMeterSharky } from '../../../../shared/api-clients/_api';
import { useRoutePaths } from '../../../../shared/context/paths';
import { enumToArray, generateSelectOptions } from '../../../../shared/helpers/enumHelpers';
import { useValidatedForm } from '../../../../shared/hooks/formValidationHook';
import { InputValidator } from '../../../../shared/hooks/ValidationTypes';
import { requiredTextInputValidator, requiredTrueValidator } from '../../../../shared/hooks/Validators';
import { BackButton } from '../../components/buttons/back-button';
import { Spinner } from '../../components/spinner/spinner';
import { useBeeConfig } from '../../hooks/beeConfig';
import { routePaths } from '../../routes';
import './configure-meter.scss';

export const DEFAULT_HOT_WATER_METER_ADDRESS = 82;
export const DEFAULT_HEATING_METER_ADDRESS = 80;
export const DEFAULT_METER_CHANNEL = 'C2';

interface IFlowMeterForm {
  meterType: DeviceType;
  heatFlowMeterCircuit: HeatFlowMeterCircuit;
  location: string;
  confirmed: boolean;
}

const heatFlowMeterCircuitOptions = {
  [HeatFlowMeterCircuit.HOT_WATER]: 'Hot water',
  [HeatFlowMeterCircuit.HEATING]: 'Heating',
};

const heatFlowMeterCircuitReviewText = {
  [HeatFlowMeterCircuit.HOT_WATER]: [
    'The heat meter is installed on the cold feed into the combi boiler or cylinder',
    'The temperature probe is installed on the hot flow from the cylinder or combi boiler',
  ],
  [HeatFlowMeterCircuit.HEATING]: [
    'The heat meter is installed on the primary return to heat source',
    'The temperature probe is installed on the primary flow from the heat source.',
  ],
};

const validators: InputValidator<IFlowMeterForm>[] = [
  {
    key: 'meterType',
    validators: [
      {
        validate: value => enumToArray(DeviceType, false, DeviceType.SHARKY).includes(value),
        message: 'Must be a valid meter type',
      },
    ],
  },
  {
    key: 'heatFlowMeterCircuit',
    validators: [
      {
        validate: value => enumToArray(HeatFlowMeterCircuit).includes(value),
        message: 'Must be a valid heat flow meter circuit',
      },
    ],
  },
  {
    key: 'location',
    validators: [requiredTextInputValidator],
  },
  {
    key: 'confirmed',
    validators: [requiredTrueValidator('You must confirm that you have read the instructions')],
  },
];

export const ConfigureHeatFlowMeterView: React.FunctionComponent = () => {
  const { heatFlowMeterCircuit } = useParams<{ heatFlowMeterCircuit: string }>();
  const { createDevice, loading, getDevicesByCategory, editDevice, removeDevice } = useBeeConfig();
  const { push } = useRoutePaths();

  const existingDevices = getDevicesByCategory<HeatFlowMeterSharky>(RuleCategory.HEAT_FLOW);
  const existingDevice = existingDevices.find(device => device.params.heat_flow_meter_circuit === heatFlowMeterCircuit);

  const initialState: IFlowMeterForm = React.useMemo(
    () => ({
      heatFlowMeterCircuit: existingDevice?.params.heat_flow_meter_circuit ?? null,
      meterType: DeviceType.SHARKY,
      location: existingDevice?.params.install_location ?? null,
      confirmed: false,
    }),
    [existingDevice]
  );
  const { DataForm, bind, dataBinder } = useForm(initialState);
  const { setTouched, formIsValid, validationErrors } = useValidatedForm<IFlowMeterForm>(validators, dataBinder);

  const handleNext = () => {
    // Should never get here
    const meterType = dataBinder.getValue('meterType');
    if (meterType !== DeviceType.SHARKY) {
      throw Error('Only Sharky meters are supported at this time');
    }
    const device: HeatFlowMeterSharky = {
      deviceType: meterType,
      params: {
        ...existingDevice?.params,
        address:
          dataBinder.getValue('heatFlowMeterCircuit') === HeatFlowMeterCircuit.HOT_WATER
            ? DEFAULT_HOT_WATER_METER_ADDRESS
            : DEFAULT_HEATING_METER_ADDRESS,
        channel: DEFAULT_METER_CHANNEL,
        heat_flow_meter_circuit: dataBinder.getValue('heatFlowMeterCircuit'),
        install_location: dataBinder.getValue('location'),
        category: RuleCategory.HEAT_FLOW,
        action_type: RuleActionType.MONITORING,
      },
    };
    if (existingDevice) {
      editDevice(existingDevice, device);
    } else {
      createDevice(device);
    }
    push(routePaths.heatAndFlowMeters.root);
  };

  const deletable = useButtonConfirmDialog({
    content: `Are you sure you want to delete the "${existingDevice?.deviceType} ${existingDevice?.params?.install_location}" heat flow meter?`,
    confirmText: 'Confirm',
    cancelText: 'Cancel',
  });

  const handleDelete = () => {
    removeDevice(existingDevice);
    push(routePaths.heatAndFlowMeters.root);
  };

  const meterTypeOptions = generateSelectOptions(DeviceType, false, DeviceType.SHARKY);

  return (
    <DataForm validationResults={validationErrors} validationMode="both">
      {loading && <Spinner />}
      <BackButton />
      <h1>
        Configuration <span className="color-primary">Heat and flow meter</span>
      </h1>
      <div>
        <SelectInput
          className="configure-meter-form__input"
          {...bind.select('meterType')}
          label="What type of meter is being installed?"
          options={meterTypeOptions}
          onBlur={setTouched}
          data-qa="select-meter-type"
        />
      </div>
      <div className="configure-meter-form__input">
        <label>On which circuit is this heat meter installed?</label>
        <div className="configure-meter__radio-button">
          {Object.entries(heatFlowMeterCircuitOptions).map(([heatFlowMeterOption, label]) => {
            const isDisabled = !!existingDevices.find(
              device => device.params.heat_flow_meter_circuit === heatFlowMeterOption
            );
            return (
              <div
                className={`radio-button ${isDisabled ? 'configure-meter__radio-button--disabled' : ''}`}
                key={heatFlowMeterOption}
                data-qa={`configure-heat-flow-meter-circuit__${heatFlowMeterOption}`}
              >
                <RadioInput
                  labelContent={label}
                  value={heatFlowMeterOption}
                  {...bind.radio('heatFlowMeterCircuit', heatFlowMeterOption)}
                  onBlur={setTouched}
                  disabled={isDisabled}
                />
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <TextInput
          className="configure-meter-form__input"
          {...bind.text('location')}
          label="Where the device installed?"
          onBlur={setTouched}
          data-qa="text-location"
        />
      </div>
      {dataBinder.getValue('heatFlowMeterCircuit') && (
        <div>
          <p>Please confirm the following before continuing:</p>
          <ul className="configure-meter__review" data-qa="review-text">
            {heatFlowMeterCircuitReviewText[dataBinder.getValue('heatFlowMeterCircuit')].map(
              (text: string, i: number) => (
                <div key={text}>
                  <div className="configure-meter__reviewListItem">
                    <li>{text}</li>
                  </div>
                  {i < Object.keys(heatFlowMeterCircuitReviewText).length - 1 && (
                    <div className="configure-meter__reviewDivider" />
                  )}
                </div>
              )
            )}
          </ul>
        </div>
      )}
      <div className="configure-meter__confirmation">
        <CheckboxInput
          {...bind.checkbox('confirmed')}
          labelContent="I confirm I've checked all of the above"
          onBlur={setTouched}
          data-qa="checkbox-accept"
        />
      </div>

      <Row className="btn-row">
        {existingDevice && (
          <Button
            className="btn-full secondary-button delete-button"
            onClick={async () => {
              if (await deletable()) {
                handleDelete();
              }
            }}
            data-qa="delete-button"
          >
            Delete Heat Flow Meter
          </Button>
        )}

        <Button
          className="btn-full inline-meters-button"
          onClick={handleNext}
          disabled={!formIsValid}
          data-qa="continue-button"
        >
          Next
        </Button>
      </Row>
    </DataForm>
  );
};
